interface IUsePiratesMarketStatus {
  modalStatus: ComputedRef<string>;
}

export default (): IUsePiratesMarketStatus => {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  const { t } = useI18n();
  const isCountdownHidden = ref(false);
  const date = ref(new Date());

  // Get nearest UTC boundary based on step hours (1 hour for pirates market)
  const getNearestUTCDate = (now: Date, stepHours = 1): Date => {
    const year = now.getUTCFullYear();
    const month = now.getUTCMonth();
    const day = now.getUTCDate();
    const hour = now.getUTCHours();

    const boundaryStartHour = Math.floor(hour / stepHours) * stepHours;
    const candidate = new Date(Date.UTC(year, month, day, boundaryStartHour, 0, 0, 0));

    // If we are exactly at the boundary, return it; otherwise return the next boundary in the future
    if (now.getTime() <= candidate.getTime()) {
      return candidate;
    }

    const next = new Date(candidate);
    next.setUTCHours(candidate.getUTCHours() + stepHours);
    return next;
  };

  const timeEnd = computed(() => {
    return getNearestUTCDate(date.value, 1); // 1 hour intervals
  });

  const { timeLeft, restartCountdown } = useCountdownDate(timeEnd, 'second');

  const generateCountdownMessage = computed(() => {
    const { hours, minutes, seconds } = timeLeft;

    if (hours) {
      return `${t('sellerUpdatesIn')}\n${t('coreHoursMin', { hours: hours, minutes: minutes })}`;
    }

    if (minutes) {
      return `${t('sellerUpdatesIn')}\n${t('coreMin', { value: minutes })} ${t('coreSec', { value: seconds })}`;
    }

    if (seconds) {
      return `${t('sellerUpdatesIn')}\n${t('coreSec', { value: seconds })}`;
    }

    return '';
  });

  const modalStatus = computed(() => {
    if (timeLeft.ended) {
      return t('sellerUpdating');
    }

    return generateCountdownMessage.value;
  });

  const createTimer = (ms: number) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    timeoutId = setTimeout(async () => {
      isCountdownHidden.value = false;
      date.value = new Date();
      restartCountdown();
    }, ms);
  };

  const calculateTriggerDelay = () => {
    const now = new Date();

    // Find the last 1-hour UTC boundary at or before now
    const stepHours = 1;
    const year = now.getUTCFullYear();
    const month = now.getUTCMonth();
    const day = now.getUTCDate();
    const hour = now.getUTCHours();
    const boundaryStartHour = Math.floor(hour / stepHours) * stepHours;
    const lastBoundary = new Date(Date.UTC(year, month, day, boundaryStartHour, 0, 0, 0));

    // The "updating" window lasts 1 minute after the boundary
    const endOfOpening = new Date(lastBoundary);
    endOfOpening.setUTCMinutes(endOfOpening.getUTCMinutes() + 1);

    return endOfOpening.getTime() - now.getTime();
  };

  watch(
    () => timeLeft.ended,
    (isEnded) => {
      if (!isEnded) {
        return;
      }

      isCountdownHidden.value = true;

      const oneMinuteMs = 60 * 1000;
      createTimer(oneMinuteMs);
    }
  );

  watch(generateCountdownMessage, (message) => {
    isCountdownHidden.value = !message;
  });

  onMounted(async () => {
    const ms = calculateTriggerDelay();
    if (ms > 0) {
      createTimer(ms);
    }
  });

  return {
    modalStatus
  };
};
